import { z } from 'zod';
import { isoDateTime } from './common';

/**
 * Gallery items — photos (and eventually videos) from past MTC sessions.
 * Single chronological flow ordered by `takenAt` desc, then by `displayOrder`
 * for fine-grained admin reordering.
 */

export const galleryItemKindSchema = z.enum(['image', 'video']);
export type GalleryItemKind = z.infer<typeof galleryItemKindSchema>;

/**
 * Visibility model — 3 levels:
 * - `public`  → readable by anyone (default; equivalent to the old `published: true`)
 * - `members` → readable by any signed-in member (Firestore rule), but the
 *   client UI only surfaces them on the Mes-inscriptions page filtered to
 *   the member's own sessions. Use this for photos featuring participants.
 * - `hidden`  → admin-only (equivalent to the old `published: false`)
 */
export const galleryVisibilitySchema = z.enum(['public', 'members', 'hidden']);
export type GalleryVisibility = z.infer<typeof galleryVisibilitySchema>;

export const galleryItemInputSchema = z.object({
  kind: galleryItemKindSchema.default('image'),
  /** Public download URL (Firebase Storage). */
  url: z.string().url(),
  /** Optional shorter caption, shown on hover / in lightbox. */
  caption: z.string().max(280).optional(),
  /** Optional event/formation context — free text. Now mostly superseded by
   *  the FK fields below but kept for legacy items. */
  eventLabel: z.string().max(120).optional(),
  /** FK → `formations/{slug}.slug` — the formation this photo belongs to. */
  formationId: z.string().max(120).optional(),
  /** FK → one of the IDs in `formations.sessions[].id`. Free-form to avoid
   *  cascading constraints when sessions are renamed/removed. */
  sessionId: z.string().max(120).optional(),
  /** When the photo was taken — controls chronological order. */
  takenAt: isoDateTime.optional(),
  /** Manual override for ordering within the same `takenAt`. Lower = earlier. */
  displayOrder: z.number().int().default(100),
  /** Surfaced on the home page carousel when true. */
  featuredOnHome: z.boolean().default(false),
  /** Visibility level — see `galleryVisibilitySchema`. */
  visibility: galleryVisibilitySchema.default('public'),
  /** Legacy: kept for backwards-compat with the 47 pre-tagged photos.
   *  New code should read `visibility` instead. */
  published: z.boolean().default(true),
});
export type GalleryItemInput = z.infer<typeof galleryItemInputSchema>;

export const galleryItemSchema = galleryItemInputSchema.extend({
  id: z.string(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type GalleryItem = z.infer<typeof galleryItemSchema>;
